/* eslint-disable @typescript-eslint/member-ordering */
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ContractMinDaysStatus } from 'src/app/margin-calculator/models/contract-min-days-status.model';
import { OfferStatus } from 'src/app/shared/models/enums/offers.enum';
import {
    Contracts,
    DetailLookups,
    Expertise,
    FacilityLookups,
    Offer,
    OfferDetails,
    OfferJobDetails,
    OfferNotes,
    OfferRates,
    OfferType,
    ProfessionTypes,
    RTOApprovalStatuses,
    ShiftTimes,
    ShiftTypes,
    VendorLookups
} from 'src/app/shared/models/offers.model';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import { SharedOffersService as OffersHelper } from 'src/app/shared/services/offers.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { BannerComponent } from '../../banner.component';
import { RecruiterBonusComponent } from '../../components/recruiter-bonus/recruiter-bonus.component';
import { RtoDatesHelpers } from '../../helpers';
import { ContractType } from '../../models/enums/contract-type.enum';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { RtoWarningMessageComponent } from '../../rto-alerts/rto-warning-message/rto-warning-message';
import { LDFeatureManager } from '../../services/ld-feature-manager';

const DEFAULT_EXTENSION_CONTRACT_TYPE = 2;
@UntilDestroy()
@Component({
    selector: 'nova-shared-offer-create-submittal',
    templateUrl: './offer-create-submittal.component.html',
    styleUrls: ['./offer-create-submittal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatRadioModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatMenuModule,
        MatCardModule,
        RecruiterBonusComponent,
        RtoWarningMessageComponent,
        BannerComponent,
        TimePickerModule
    ]
})
export class SharedOfferCreateSubmittalComponent implements OnInit, OnChanges {
    _offer: Offer;
    _offerDetails: OfferDetails;
    lookups: DetailLookups;
    _expertise: Expertise[];
    _updatedOfferJobDetails: OfferJobDetails;
    _offerRates: OfferRates;
    _vendorRate: number;
    _vendorLookups: VendorLookups;
    facilityLookup: FacilityLookups;
    offerTypes = OfferType;
    readonly featureFlag = FeatureFlag;
    invalidChars = ['-', '+', 'e'];

    @ViewChild('clearCalendarTrigger') clearCalendarTrigger: MatMenuTrigger;
    @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;

    @Input() submittalId: number;
    @Input() vendorContactId: number;
    @Input() contractLength: number;
    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() disableSubmitAndCancel: boolean = false;
    @Input() offerType = OfferType.Agency;
    @Input() contractType: ContractType;
    @Input() isJobExtension: boolean = false;
    @Input() isJobApproved: boolean = false;
    @Input()
    set offerDetails(offerDetails: OfferDetails) {
        this._offerDetails = offerDetails;
        if (this._offerDetails) {
            this.patchEditValues();
            this.initContractLengthDaysAndWeeks(offerDetails);
        }
    }

    get offerDetails(): OfferDetails {
        return this._offerDetails;
    }

    @Input()
    set detailLookups(detailLookups: DetailLookups) {
        if (detailLookups) {
            this.professionTypes = detailLookups.professionTypes;
            this.lookups = detailLookups;
            if (this.offerType === OfferType.Direct) {
                this.lookups.contracts = this.lookups.contracts.filter((x) => x.code !== 'COC');
            }
        }
    }

    get detailLookups(): DetailLookups {
        return this.lookups;
    }

    @Input()
    set facilityLookups(facilityLookups: FacilityLookups) {
        if (facilityLookups) {
            this.facilityLookup = facilityLookups;
            if (facilityLookups.jobs) {
                this.detailsFormGroup.get('jobId').enable();
                this.filteredJobIdOptions = this.detailsFormGroup.get('jobId').valueChanges.pipe(
                    startWith(''),
                    map((val) => this.filterJobIds(val))
                );
            }
            const professionId = this.detailsFormGroup.get('professionId').value;
            if (!professionId) {
                this.detailsFormGroup.patchValue({
                    expertiseId: null
                });
            }
        }
    }

    get facilityLookups(): FacilityLookups {
        return this.facilityLookup;
    }

    @Input()
    set updatedOfferJobDetails(updatedOfferJobDetails: OfferJobDetails) {
        if (updatedOfferJobDetails) {
            this._updatedOfferJobDetails = updatedOfferJobDetails;
            const timeStart = dayjs().toDate();
            const timeEnd = dayjs().toDate();
            const timeAltStart = dayjs().toDate();
            const timeAltEnd = dayjs().toDate();

            timeStart.setHours(updatedOfferJobDetails.shiftH);
            timeStart.setMinutes(updatedOfferJobDetails.shiftM);

            timeEnd.setHours(updatedOfferJobDetails.shiftToH);
            timeEnd.setMinutes(updatedOfferJobDetails.shiftToM);

            timeAltStart.setHours(updatedOfferJobDetails.shiftAltH);
            timeAltStart.setMinutes(updatedOfferJobDetails.shiftAltM);

            timeAltEnd.setHours(updatedOfferJobDetails.shiftAltToH);
            timeAltEnd.setMinutes(updatedOfferJobDetails.shiftAltToM);

            this.detailsFormGroup.patchValue({
                ...updatedOfferJobDetails,
                shiftTimeStart: timeStart,
                shiftTimeEnd: timeEnd,
                shiftAltTimeStart: timeAltStart,
                shiftAltTimeEnd: timeAltEnd,
                endDate: new Date(updatedOfferJobDetails.endDate)
            });

            this.professionSelection(updatedOfferJobDetails.professionId, false);

            this.detailsFormGroup.patchValue({
                expertiseId: updatedOfferJobDetails.expertiseId
            });
        }
    }

    get updatedOfferJobDetails(): OfferJobDetails {
        return this._updatedOfferJobDetails;
    }

    @Input()
    set expertise(expertise: Expertise[]) {
        this._expertise = expertise;
        if (this.detailsFormGroup) {
            const expId = this.detailsFormGroup.get('expertiseId').value;
            if (!this._expertise.some((e) => e.id === expId)) {
                this.detailsFormGroup.patchValue({ expertiseId: null });
            }
        }
    }

    get expertise(): Expertise[] {
        return this._expertise;
    }

    @Input()
    set updatedRates(rates: OfferRates) {
        if (rates) {
            this._offerRates = rates;
            this.detailsFormGroup.patchValue({ facilityRate: rates.facilityRate });
            this.detailsFormGroup.patchValue({ vendorRate: rates.vendorRate });
        }
    }

    get updatedRates(): OfferRates {
        return this._offerRates;
    }

    @Input()
    set updatedVendorRate(vendorRate: number) {
        if (vendorRate !== undefined) {
            this._vendorRate = vendorRate;
            this.detailsFormGroup.patchValue({ vendorRate });
        }
    }

    get updatedVendorRate(): number {
        return this._vendorRate;
    }

    @Input()
    set vendorLookups(lookups: VendorLookups) {
        if (lookups && this.offerType === OfferType.Agency) {
            this._vendorLookups = lookups;
            const activeVendorContact = this._vendorLookups.contacts.find(
                (vl) => vl.id === this.detailsFormGroup.get('vendorContactId').value
            );

            if (!isEmpty(this.vendorContactId)) {
                this.detailsFormGroup.patchValue({ vendorContactId: +this.vendorContactId });
            } else if (!activeVendorContact) {
                this.detailsFormGroup.patchValue({ vendorContactId: '' });
            }
        }
    }

    @Input()
    set contractMinDaysStatus(contractMinDaysStatus: ContractMinDaysStatus) {
        if (contractMinDaysStatus) {
            this.contractLengthDaysApprovalRequired = false;
            this.detailsFormGroup.get('contractLengthDays').patchValue(contractMinDaysStatus.contractDays);

            if (!!contractMinDaysStatus.approvalRequired) {
                this.contractLengthDaysInfoMessage = `Contract does not meet the ${contractMinDaysStatus.contractMinDays}
                day minimum for the state of ${contractMinDaysStatus.stateAbbreviation}.
                The Base Bill Rate for the contract length will need approval after submittal.`;
                this.contractLengthDaysApprovalRequired = true;
            }
        }
    }

    get vendorLookups(): VendorLookups {
        return this._vendorLookups;
    }

    @Output() professionSelectionChange = new EventEmitter();
    @Output() jobIdSelectionChange = new EventEmitter();
    @Output() updateRatesEmit = new EventEmitter();
    @Output() updateVendorRateEmit = new EventEmitter();
    @Output() createOfferEmit = new EventEmitter();
    @Output() cancelOfferEmit = new EventEmitter();
    @Output() calculateContractDetails = new EventEmitter();
    detailsFormGroup: UntypedFormGroup;
    contracts: Contracts[] = [];
    shiftTimes: ShiftTimes[] = [];
    shiftTypes: ShiftTypes[] = [];
    professionTypes: ProfessionTypes[] = [];
    filteredJobIdOptions: Observable<number[]>;
    shiftValues = [1, 2, 3, 4, 5, 6];
    hoursValue = [7.5, 8, 9.5, 10, 11.5, 12];
    minDate: Date = dayjs().toDate();
    maxDate: Date = null;
    jobIds: number[] = [];
    viewAltShift = false;
    isRotatingShift = false;
    statusEnum = OfferStatus;
    offerNotes: OfferNotes[] = [];
    enteredNoteText = '';
    daysSelected: any[] = [];
    lengthValue = null;
    daysRequested = '';
    minEndDate = new Date();
    constDateSelected = 'date-selected';
    constDateContractStartEndClass = 'date-contract-start-or-end';
    contractLengthDaysInfoMessage = '';
    contractLengthDaysApprovalRequired = false;
    showRTOAlertOnStartEndDates = false;
    enableRtoAlertMessageFlag = toSignal(
        this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            ? this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            : of(false)
    );

    candidateJobProfessionValidator = (control: AbstractControl): ValidationErrors | null => {
        const professionId = control.get('professionId');
        const candidateProfessionId = control.get('candidateProfessionId');

        const result =
            professionId && candidateProfessionId && professionId.value !== candidateProfessionId.value
                ? { candidateJobProfessionDifferent: true }
                : null;
        return result;
    };

    constructor(
        private readonly _renderer: Renderer2,
        private readonly sharedService: SharedOffersService,
        private readonly _sharedOffersHelper: OffersHelper,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _featureManager: LDFeatureManager
    ) {}

    ngOnInit() {
        this.detailsFormGroup = this._formBuilder.group({
            id: new UntypedFormControl(''),
            candidateId: new UntypedFormControl(''),
            candidateEmail: new UntypedFormControl(''),
            facilityId: new UntypedFormControl(''),
            contractLength: new UntypedFormControl(''),
            contractLengthDays: new UntypedFormControl(''),
            contractTypeId: new UntypedFormControl(''),
            positionTypeId: new UntypedFormControl(''),
            positionType: new UntypedFormControl(''),
            jobId: new UntypedFormControl({ value: '', disabled: true }),
            professionId: new UntypedFormControl(''),
            professionName: new UntypedFormControl(''),
            candidateProfessionId: new UntypedFormControl(''),
            candidateProfessionName: new UntypedFormControl(''),
            expertiseId: new UntypedFormControl(''),
            startDate: new UntypedFormControl(''),
            endDate: new UntypedFormControl(''),
            shifts: new UntypedFormControl(''),
            shiftTypeId: new UntypedFormControl(''),
            hasRequestedTimeOff: new UntypedFormControl(''),
            shiftsAlt: new UntypedFormControl(''),
            hours: new UntypedFormControl(''),
            hoursAlt: new UntypedFormControl(''),
            shiftTimeOfDayId: new UntypedFormControl(''),
            shiftAltTimeOfDayId: new UntypedFormControl(''),
            shiftTimeStart: new UntypedFormControl(''),
            shiftTimeEnd: new UntypedFormControl(''),
            shiftAltTimeStart: new UntypedFormControl(''),
            shiftAltTimeEnd: new UntypedFormControl(''),
            shiftM: new UntypedFormControl(''),
            shiftH: new UntypedFormControl(''),
            shiftToM: new UntypedFormControl(''),
            shiftToH: new UntypedFormControl(''),
            shiftAltM: new UntypedFormControl(''),
            shiftAltH: new UntypedFormControl(''),
            shiftAltToM: new UntypedFormControl(''),
            shiftAltToH: new UntypedFormControl(''),
            shiftDetails: new UntypedFormControl(''),
            requestedTimeOff: new UntypedFormControl(''),
            requestedTimeOffIsApproved: new UntypedFormControl(''),
            vendorRate: new UntypedFormControl(''),
            facilityRate: new UntypedFormControl(''),
            otherDetails: new UntypedFormControl(''),
            vendorSubmittalId: new UntypedFormControl(null),
            agencyOfferNotes: new UntypedFormControl(''),
            offerType: new UntypedFormControl(this.offerType),
            submittalJobId: new UntypedFormControl(null),
            rtoApprovalStatus: new UntypedFormControl(''),
            vendorContactId: new UntypedFormControl(''),
            isRemoteBooking: new UntypedFormControl(''),
            ayaPaidBonus: new UntypedFormControl('')
        });

        if (this.offerType === OfferType.Agency) {
            this.setAgencyOfferFieldsAndValidations();
        }

        const shiftTypeIdCtrl = this.detailsFormGroup.controls.shiftTypeId;
        const shiftsCtrl = this.detailsFormGroup.controls.shifts;
        const hoursCtrl = this.detailsFormGroup.controls.hours;
        const shiftTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftTimeOfDayId;

        shiftTypeIdCtrl.setValidators(Validators.required);
        shiftsCtrl.setValidators(Validators.required);
        hoursCtrl.setValidators(Validators.required);
        shiftTimeOfDayIdCtrl.setValidators(Validators.required);

        shiftTypeIdCtrl.updateValueAndValidity();
        shiftsCtrl.updateValueAndValidity();
        hoursCtrl.updateValueAndValidity();
        shiftTimeOfDayIdCtrl.updateValueAndValidity();

        if (this.detailsFormGroup) {
            this.detailsFormGroup
                .get('shiftTypeId')
                .valueChanges.pipe(untilDestroyed(this))
                .subscribe((shift) => {
                    this.validateShiftFields(shift);
                });
        }

        this.showDaysRequested();

        this.detailsFormGroup.addValidators(this.candidateJobProfessionValidator);

        this.setContractDateFieldsValidations();
    }

    get validateCandidateProfessionJobs(): boolean {
        const professionIdControl = this.detailsFormGroup.controls['professionId'];
        const candidateProfessionIdControl = this.detailsFormGroup.controls['candidateProfessionId'];

        return (
            professionIdControl &&
            candidateProfessionIdControl &&
            professionIdControl.value !== candidateProfessionIdControl.value
        );
    }

    private validateShiftFields(shift: any) {
        const shiftsAltCtrl = this.detailsFormGroup.controls.shiftsAlt;
        const hoursAltCtrl = this.detailsFormGroup.controls.hoursAlt;
        const shiftAltTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftAltTimeOfDayId;

        this.isRotatingShift = shift === 3;
        if (this.isRotatingShift) {
            // when flag is on, clear alt shifts, hours values for rotating shift
            shiftsAltCtrl.patchValue('');
            hoursAltCtrl.patchValue('');
        }

        this.viewAltShift = shift === 2 ? true : false;

        shiftsAltCtrl.clearValidators();
        hoursAltCtrl.clearValidators();
        shiftAltTimeOfDayIdCtrl.clearValidators();

        if (this.isRotatingShift) {
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        } else if (this.viewAltShift) {
            shiftsAltCtrl.setValidators(Validators.required);
            hoursAltCtrl.setValidators(Validators.required);
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        }

        shiftsAltCtrl.updateValueAndValidity();
        hoursAltCtrl.updateValueAndValidity();
        shiftAltTimeOfDayIdCtrl.updateValueAndValidity();
    }

    setAgencyOfferFieldsAndValidations(): void {
        this.detailsFormGroup.get('vendorContactId').setValidators([Validators.required]);
    }

    setContractDateFieldsValidations(): void {
        if (this.detailsFormGroup) {
            this.detailsFormGroup.get('startDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('endDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLength').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLengthDays').setValidators([Validators.required]);
        }
    }

    hasError = (controlName: string, errorName: string) => {
        return this.detailsFormGroup.controls[controlName].hasError(errorName);
    };

    patchEditValues() {
        const timeStart = dayjs().toDate();
        const timeEnd = dayjs().toDate();
        const timeAltStart = dayjs().toDate();
        const timeAltEnd = dayjs().toDate();

        timeStart.setHours(this._offerDetails.shiftH);
        timeStart.setMinutes(this._offerDetails.shiftM);

        timeEnd.setHours(this._offerDetails.shiftToH);
        timeEnd.setMinutes(this._offerDetails.shiftToM);

        timeAltStart.setHours(this._offerDetails.shiftAltH);
        timeAltStart.setMinutes(this._offerDetails.shiftAltM);

        timeAltEnd.setHours(this._offerDetails.shiftAltToH);
        timeAltEnd.setMinutes(this._offerDetails.shiftAltToM);

        this.detailsFormGroup.patchValue({
            ...this._offerDetails,
            // candidate: `${this._offerDetails.candidateFirstName} ${this._offerDetails.candidateLastName}`,/8
            shiftTimeStart: timeStart,
            shiftTimeEnd: timeEnd,
            shiftAltTimeStart: timeAltStart,
            shiftAltTimeEnd: timeAltEnd
        });

        if (this.offerType === OfferType.Agency) {
            this.detailsFormGroup.patchValue({
                ...this._offerDetails,
                vendorSubmittalId: Number(this.submittalId)
            });
        } else {
            this.detailsFormGroup.patchValue({
                ...this._offerDetails,
                submittalJobId: Number(this.submittalId),
                rtoApprovalStatus: this._offerDetails.hasRequestedTimeOff
                    ? RTOApprovalStatuses.PendingApproval
                    : RTOApprovalStatuses.NotApproved
            });
        }

        this.viewAltShift = this._offerDetails.shiftTypeId === 2 ? true : false;

        if (!this.detailsFormGroup.get('contractLength').value) {
            this.calculateContract(null, true);
        }

        if (this._offerDetails.requestedTimeOff?.length > 0) {
            this.daysSelected = this._offerDetails.requestedTimeOff.map((date) => dayjs(date).toDate());
            this.showDaysRequested();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.contractLength && changes.contractLength.currentValue) {
            this.detailsFormGroup.get('contractLength').patchValue(changes.contractLength.currentValue);
        }
        if (changes.startDate && changes.startDate.currentValue) {
            this.detailsFormGroup.get('startDate').patchValue(changes.startDate.currentValue);
        }
        if (changes.endDate && changes.endDate.currentValue) {
            if (this.detailsFormGroup?.get('startDate').value) {
                this.detailsFormGroup.get('endDate').patchValue(changes.endDate.currentValue);
                this.validateRTODates();
            }
        }
        if (changes.facilityLookups) {
            if (this.detailsFormGroup && this.contractType === ContractType.Extension) {
                const extensionContractType =
                    this.lookups?.contracts?.find((x) => x.code === ContractType.Extension)?.id ??
                    DEFAULT_EXTENSION_CONTRACT_TYPE;
                this.detailsFormGroup.get('contractTypeId').setValue(extensionContractType);
            }
        }
    }

    filterJobIds(val: any) {
        const professionId = this.detailsFormGroup.get('professionId').value;
        const filteredJobIds = this.sharedService.filterJobIdsForCreate(val, this.facilityLookups, professionId);
        if (val) {
            const profId = this.facilityLookups.jobs.find((x) => x.jobId === val)?.professionId;

            if (profId) {
                this.professionSelectionChange.emit(profId);
            }
        }

        return filteredJobIds;
    }

    displayJobIds(id: any) {
        if (id) {
            return id;
        }
        return '';
    }

    displayFieldName(option: any) {
        if (option && option.name) {
            return option.name;
        }
        return '';
    }

    professionSelection(professionId: number, checkRateUpdateFields = true) {
        this.professionSelectionChange.emit(professionId);
        this.detailsFormGroup.patchValue({
            expertiseId: null
        });
        if (checkRateUpdateFields) {
            this.checkRateUpdateFields('Profession');
        }
    }

    jobIdSelection(event): void {
        const val = event.option?.value || event.target?.value;
        if (val) {
            this.jobIdSelectionChange.emit(val);
        }
    }

    startDateSelected(event): void {
        this.minEndDate = new Date(event.value);
        this.calculateContract();
    }

    getDuration(start: Date, end: Date) {
        const startInput = dayjs(start);
        const endInput = dayjs(end);
        const duration = dayjs(endInput).diff(startInput, 'days');
        return `${duration} days`;
    }

    get offerStartDate(): Date | null {
        if (!this.enableRtoAlertMessageFlag()) {
            return null;
        }
        return this.detailsFormGroup.get('startDate')?.value as Date | null;
    }

    get offerEndDate(): Date | null {
        if (!this.enableRtoAlertMessageFlag()) {
            return null;
        }
        return this.detailsFormGroup.get('endDate')?.value as Date | null;
    }

    createOffer() {
        const data = this.detailsFormGroup.value;
        if (data.shiftTypeId) {
            data.hasShifts = true;
        }
        if (!data.hasRequestedTimeOff) {
            data.requestedTimeOff = null;
        }
        this.createOfferEmit.emit(data);
    }

    cancelCreateOffer() {
        this.cancelOfferEmit.emit();
    }

    calculateContract(endChanged = null, firstLoad = false) {
        const obj = {
            endChanged,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            jobId: this.detailsFormGroup.get('jobId').value,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            firstLoad
        };
        this.calculateContractDetails.emit(obj);
    }

    focusOutContract() {
        const val = this.detailsFormGroup.get('contractLength').value;
        const start = this.detailsFormGroup.get('startDate').value;
        if (Number.isInteger(val)) {
            if (start) {
                this.detailsFormGroup.get('endDate').patchValue('');
            }
            this.calculateContract();
        }
    }

    updateShiftEndTime() {
        const lengthHours = this.detailsFormGroup.get('hours').value;
        const timeStart = this.detailsFormGroup.get('shiftTimeStart').value;
        if (lengthHours && timeStart) {
            const shiftEndTime = this._sharedOffersHelper.getShiftEndTime(lengthHours, timeStart);
            this.detailsFormGroup.patchValue({ shiftTimeEnd: shiftEndTime });
            this.onTimeEndChanged(shiftEndTime);
        }
    }

    updateShiftEndTimeAlt() {
        const hoursAlt = this.detailsFormGroup.get('hoursAlt').value;
        const shiftAltTimeStart = this.detailsFormGroup.get('shiftAltTimeStart').value;
        if (hoursAlt && shiftAltTimeStart) {
            const endTimeAlt = this._sharedOffersHelper.getShiftEndTime(hoursAlt, shiftAltTimeStart);
            this.detailsFormGroup.patchValue({ shiftAltTimeEnd: endTimeAlt });
            this.onAltTimeEndChanged(endTimeAlt);
        }
    }

    onShiftLengthAltSelected() {
        this.updateShiftEndTimeAlt();
    }

    onTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftH: Number(hour),
            shiftM: Number(min)
        });

        this.updateShiftEndTime();
    }

    onTimeEndChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftToH: Number(hour),
            shiftToM: Number(min)
        });
    }

    onAltTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltH: Number(hour),
            shiftAltM: Number(min)
        });

        this.updateShiftEndTimeAlt();
    }

    onAltTimeEndChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltToH: Number(hour),
            shiftAltToM: Number(min)
        });
    }

    dateClass(): any {
        if (this.enableRtoAlertMessageFlag()) {
            return (date: Date): MatCalendarCellCssClasses => {
                return RtoDatesHelpers.getCssClassForDate(
                    date,
                    this.offerStartDate,
                    this.offerEndDate,
                    this.daysSelected
                );
            };
        }
        return (date: Date): MatCalendarCellCssClasses => {
            const foundDate = this.daysSelected.find((value) => dayjs(value).toDate().getTime() === date.getTime());
            if (foundDate) {
                return this.constDateSelected;
            }
            return null;
        };
    }

    dateChanged(event: string): void {
        const foundDateIndex = this.daysSelected.findIndex((date) => dayjs(date).isSame(dayjs(event)));
        const dateNode = this._getDateNode(event);
        let formattedDate = [];

        if (foundDateIndex < 0) {
            this.daysSelected.push(event);
            this.daysSelected = this._sortDates(this.daysSelected);

            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            if (
                this.enableRtoAlertMessageFlag() &&
                RtoDatesHelpers.addCssClassForOfferStartEndDates(
                    new Date(event),
                    this.offerStartDate,
                    this.offerEndDate
                )
            ) {
                this._renderer.addClass(dateNode, this.constDateContractStartEndClass);
            } else {
                this._renderer.addClass(dateNode, this.constDateSelected);
            }
        } else {
            this.daysSelected.splice(foundDateIndex, 1);
            this.daysSelected = this._sortDates(this.daysSelected);
            formattedDate = [];
            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            this._renderer.removeClass(dateNode, this.constDateSelected);
            if (this.enableRtoAlertMessageFlag()) {
                this._renderer.removeClass(dateNode, this.constDateContractStartEndClass);
            }
        }

        this.showDaysRequested();
    }

    clearDates() {
        this.daysSelected = [];
        this.calendar.updateTodaysDate();
        this.clearCalendarTrigger.closeMenu();
        this.detailsFormGroup.get('requestedTimeOff').patchValue([]);
        this.showDaysRequested();
        this.validateRTODates();
    }

    showDaysRequested(): void {
        if (this.daysSelected.length === 0) {
            this.daysRequested = '';
            this.validateRTODates();
            return;
        }

        if (this.daysSelected.length === 1) {
            this.daysRequested = `${this._mapDatesToFormat(this.daysSelected).join('')}`;
            this.validateRTODates();
            return;
        }

        let dateRangeStr = '';
        let nonDateRangeStr = '';
        const dateRangeSet = new Set<Date[]>();
        const nonDateRangeSet = new Set<Date[]>();

        let currentConsecutive: Date[] = [];

        this.daysSelected.forEach((value) => {
            if (currentConsecutive.length === 0) {
                currentConsecutive.push(value);
                nonDateRangeSet.add(currentConsecutive);
                return;
            }

            const lastConsecutivePair = currentConsecutive[currentConsecutive.length - 1];

            if (dayjs(value).diff(lastConsecutivePair, 'days') === 1) {
                currentConsecutive.push(value);
            } else {
                currentConsecutive = [value];
            }

            if (currentConsecutive.length > 1) {
                dateRangeSet.add(currentConsecutive);

                for (const element of nonDateRangeSet) {
                    // Filter out dates from the non consecutive date range
                    if (dateRangeSet.has(element)) {
                        nonDateRangeSet.delete(element);
                    }
                }
            } else {
                nonDateRangeSet.add(currentConsecutive);
            }
        });

        dateRangeStr = this._buildDateRangeStr(dateRangeSet, false);
        nonDateRangeStr = this._buildDateRangeStr(nonDateRangeSet, true);

        if (nonDateRangeStr !== '' && dateRangeStr === '') {
            this.daysRequested = `${nonDateRangeStr}`;
        } else if (dateRangeStr !== '' && nonDateRangeStr === '') {
            this.daysRequested = `${dateRangeStr}`;
        } else {
            this.daysRequested = `${nonDateRangeStr}, ${dateRangeStr}`;
        }
        this.validateRTODates();
    }

    validateRTODates() {
        if (this.enableRtoAlertMessageFlag()) {
            this.showRTOAlertOnStartEndDates = RtoDatesHelpers.shouldDisplayRTOAlertOnStartOrEndDate(
                this.offerStartDate,
                this.offerEndDate,
                this.daysSelected
            );
        }
    }

    checkRateUpdateFields(changeField: string): void {
        if (
            !!this.detailsFormGroup.get('facilityId').value &&
            !!this.detailsFormGroup.get('professionId').value &&
            !!this.detailsFormGroup.get('expertiseId').value &&
            !!this.detailsFormGroup.get('hours').value
        ) {
            const ratesRequest = {
                facilityId: this.detailsFormGroup.get('facilityId').value,
                professionId: this.detailsFormGroup.get('professionId').value,
                expertiseId: this.detailsFormGroup.get('expertiseId').value,
                hours: this.detailsFormGroup.get('hours').value,
                shiftTimeOfDayId: this.detailsFormGroup.get('shiftTimeOfDayId').value,
                changeField
            };
            this.updateRatesEmit.emit(ratesRequest);
        }
        if (changeField === 'Shift Length') {
            this.updateShiftEndTime();
        }
    }

    updateVendorRate(): void {
        if (this.offerType === OfferType.Agency) {
            const vendorRateRequest = {
                facilityId: this.detailsFormGroup.get('facilityId').value,
                facilityRate: this.detailsFormGroup.get('facilityRate').value
            };
            this.updateVendorRateEmit.emit(vendorRateRequest);
        }
    }

    private _buildDateRangeStr(values: Set<Date[]>, nonConsecutive: boolean): string {
        let count = 0;
        let str = '';

        for (const element of values) {
            const array = nonConsecutive ? [] : this._mapDatesToFormat(Array.of(element)[0]);

            if (count === values.size - 1) {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}`
                    : `${array[0]}-${array[array.length - 1]}`;
            } else {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}, `
                    : `${array[0]}-${array[array.length - 1]}, `;
            }

            count++;
        }

        return str;
    }

    private _getDateNode(date: string): unknown {
        return this._renderer.selectRootElement(`td button[aria-label="${dayjs(date).format('MMMM D, YYYY')}"]`, true);
    }

    private _mapDatesToFormat(dates: Date[], format?: string): string[] {
        return dates.map((date) => dayjs(date).format(format && format !== '' ? format : 'M/D/YY'));
    }

    private _sortDates(dates: Date[]): Date[] {
        return dates.sort((first, second) => dayjs.utc(first).diff(second));
    }

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }

    initContractLengthDaysAndWeeks(offer: OfferDetails) {
        const obj = {
            changeType: 'initialLoad',
            startDate: offer.startDate,
            endDate: offer.endDate,
            numOfShifts: offer.shifts,
            facilityRate: offer.facilityRate
        };
        this.calculateContractDetails.emit(obj);
    }

    calculateContractDates(changeType: string): void {
        const obj = {
            changeType: changeType,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            contractLengthDays: this.detailsFormGroup.get('contractLengthDays').value,
            numOfShifts: this.detailsFormGroup.get('shifts').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value
        };
        this.calculateContractDetails.emit(obj);
        if (this.enableRtoAlertMessageFlag()) {
            this.showDaysRequested();
        }
    }

    validateInput(event) {
        if (this.invalidChars.includes(event.key)) {
            event.preventDefault();
        }
    }
}
